import * as React from "react";
import { Card, createStyles, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import TextFieldNumeric from "../TextFieldNumeric";
import { getDateFromTimezonePtBrToGMT } from "../../helpers/datetime";
import { castNumberTo2Decimals, formatValue, isNumberValid } from "../../helpers/utility";
import clsx from "clsx";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "padding": theme.spacing(2),
      "maxWidth": 360,
      "flex": 1,

      [theme.breakpoints.down("md")]: {
        margin: "auto",
      },

      "& .flex-grow": {
        flexGrow: 1,
      },
    },

    flexBoxRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },

    flexBoxColumn: {
      "display": "flex",
      "flexDirection": "column",

      "& > *": {
        lineHeight: 1,
      },
    },

    title: {
      "borderBottom": `0.2px dotted ${theme.customPalette.border.gray}`,
      "color": theme.palette.primary.main,

      "& .main": {
        fontSize: "1.5rem",
      },

      "& .secondary-label": {
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
      },

      "& .secondary": {
        fontSize: "1.75rem",
      },
    },

    infoWrapper: {
      "marginTop": 2,
      "color": theme.palette.text.primary,

      "& .main-value": {
        fontSize: "1.5rem",
      },

      "& .uom": {
        fontSize: "1rem",
      },

      "& .text-align-right": {
        textAlign: "right",
      },

      "& .date": {
        fontSize: "1.25rem",
        marginRight: theme.spacing(0.5),
      },
    },
    inputWrapper: {
      marginTop: theme.spacing(1),
      display: "flex",
      alignItems: "center",
      columnGap: theme.spacing(1),
    },
  })
);

type InstallationTankLevelReportCardProps = {
  id?: string;
  rtu?: string;
  capacity?: number;
  capacityUom?: string;
  max?: number;
  maxUom?: string;
  lastReadingDate?: Date;
  lastReading?: number;
  lastReadingUom?: string;
  inputValue?: string;
  error?: boolean;
  inputHelperText?: string;
  handleChangeInputValue?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

function InstallationTankLevelReportCard(props: InstallationTankLevelReportCardProps) {
  const {
    id,
    rtu,
    capacity,
    capacityUom,
    max,
    maxUom,
    lastReadingDate,
    lastReading,
    lastReadingUom,
    inputHelperText,
    inputValue = "",
    error = false,
    handleChangeInputValue,
  } = props;

  const classes = useStyles();

  return (
    <Card className={classes.root} elevation={5}>
      {/* Header */}
      <div className={clsx(classes.flexBoxRow, classes.title)}>
        <Typography className="main">{`Tanque ID ${id}`}</Typography>

        <div className={classes.flexBoxColumn}>
          <Typography className="secondary-label">Serial RTU</Typography>
          <Typography className="secondary">{rtu}</Typography>
        </div>
      </div>

      {/* Content */}
      <Grid className={classes.infoWrapper} container spacing={2}>
        <Grid className={classes.flexBoxRow} item container>
          <Grid item>
            <Typography>Capacidade do Tanque</Typography>
            <Typography className="main-value">
              {formatValue(capacity, 2)}
              <span className="uom"> {capacityUom}</span>
            </Typography>
          </Grid>

          <Grid item>
            <Typography>Max Gauge</Typography>
            <Typography className="main-value">
              {formatValue(max, 2)}
              <span className="uom"> {maxUom}</span>
            </Typography>
          </Grid>
        </Grid>

        <Grid className={classes.flexBoxRow} item container>
          <Grid item>
            <Typography>Última Leitura</Typography>
            <Typography className="main-value">
              {formatValue(lastReading, 2)}
              <span className="uom"> {lastReadingUom}</span>
            </Typography>
          </Grid>

          <Grid item>
            <Typography className="text-align-right">Data de Leitura</Typography>
            <Typography>
              <span className="date"> {getDateFromTimezonePtBrToGMT(lastReadingDate)}</span>
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <div className={classes.inputWrapper}>
        <Typography>Leitura:</Typography>
        <TextFieldNumeric
          className="flex-grow"
          name={id}
          value={isNumberValid(inputValue) ? castNumberTo2Decimals(Number(inputValue)) : ""}
          onChange={handleChangeInputValue}
          max={castNumberTo2Decimals(Number(max))}
          min={0}
          endAdornment={<span>Pol.</span>}
          error={error}
          helperText={inputHelperText}
        />
      </div>
    </Card>
  );
}

export default InstallationTankLevelReportCard;
